import { Controller, useForm } from "react-hook-form";
import countries from "../../constants/countries.json";
import InputWithLabel from "../InputWithLabel";
import Dropdown from "../DropDown";
import CountryDialCodeSelect from "./CountryDialCodeSelect";
import {
  useAddVendorContactMutation,
  useGetVendorContactsQuery,
  useUpdateVendorContactMutation,
} from "../../store/apiSlices/vendorContacts";
import { toast } from "react-toastify";
import { SpinnerIcon } from "../Icons";

interface FormValues {
  name: string;
  country: string;
  role: string;
  company: string;
  email: string;
  phone: string;
  dialCode: string;
}

const initialState = {
  company: "",
  country: "",
  dialCode: "",
  email: "",
  name: "",
  phone: "",
  role: "",
};

export default function ContactForm({
  toggleModal,
  contactId,
}: {
  toggleModal: () => void;
  contactId?: string;
}) {
  const { data: contacts } = useGetVendorContactsQuery();

  const [addVendorContact] = useAddVendorContactMutation();
  const [updateVendorContact] = useUpdateVendorContactMutation();

  const contact = contacts?.find((el) => el.id === parseInt(contactId!));

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<FormValues>({
    defaultValues: {
      dialCode: "+91",
    },
    values: contact
      ? {
          ...contact,
          dialCode: contact?.phone?.split("-").at(0)! || "",
          phone: contact?.phone?.split("-").at(1)! || "",
        }
      : initialState,
  });

  const onSubmit = async (values: FormValues) => {
    const { phone, dialCode, ...rest } = values;
    const payload = {
      phone: `${dialCode}-${phone}`,
      ...rest,
    };

    if (contact) {
      const { data } = await updateVendorContact({
        id: contact.id,
        ...payload,
      });

      if (data.status === "success") {
        toast.success(data.message || "Contact updated successfully!");
        toggleModal();
      } else {
        toast.error("Something went wrong!");
      }
    } else {
      const { data } = await addVendorContact(payload);

      if (data.status === "success" && data.id) {
        toast.success(data.message || "Contact added successfully!");
        toggleModal();
      } else {
        toast.error("Something went wrong!");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-3">
      {/* Name */}
      <InputWithLabel
        label="Full Name"
        placeholder="Enter your full name"
        error={errors.name?.message}
        {...register("name", { required: "Name is required" })}
      />

      {/* Country */}
      <Dropdown
        label="Country"
        placeholder="Select a country"
        options={countries.map((c) => ({ label: c.name, value: c.iso2 }))}
        {...register("country", { required: "Country is required" })}
        error={errors.country?.message}
      />

      {/* Role */}
      <InputWithLabel
        label="Role"
        placeholder="Enter your role"
        error={errors.role?.message}
        {...register("role", { required: "Role is required" })}
      />

      {/* Company */}
      <InputWithLabel
        label="Company"
        placeholder="Enter your company"
        error={errors.company?.message}
        {...register("company", { required: "Company is required" })}
      />

      {/* Email */}
      <InputWithLabel
        label="Email"
        type="email"
        placeholder="Enter your email"
        error={errors.email?.message}
        {...register("email", {
          required: "Email is required",
          pattern: {
            value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
            message: "Invalid email format",
          },
        })}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Phone Number
        </label>
        <div className="flex gap-1">
          {/* Dial code dropdown */}
          <Controller
            name="dialCode"
            control={control}
            render={({ field }) => (
              <div className="w-24">
                <CountryDialCodeSelect
                  value={field.value}
                  onChange={field.onChange}
                />
              </div>
            )}
          />

          {/* Phone input */}
          <div className="flex-1">
            <InputWithLabel
              label=""
              placeholder="Enter here"
              {...register("phone", {
                required: "Phone number is required",
                pattern: {
                  value: /^[0-9]{5,15}$/,
                  message: "Enter valid phone number",
                },
              })}
            />
          </div>
        </div>
        <p className="text-xs -mt-2 text-red-500">{errors.phone?.message}</p>
      </div>

      <button
        type="submit"
        className="w-full mt-3 col-span-2 bg-custom-blue-medium text-white font-medium py-2 px-4 rounded-md hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-custom-blue-medium/60"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600 inline-flex justify-center" />
        ) : (
          "Submit"
        )}
      </button>
    </form>
  );
}
